/** Printable skill tree: structured document with brand styling (not a screenshot of the graph). */

export interface SkillTreePrintNode {
  title: string;
  description: string | null;
  isKeyNode: boolean;
  contentSummary: string | null;
  prereqTitles: string[];
}

export interface SkillTreePrintGroup {
  kind: "chapter" | "level";
  badge: string;
  title: string;
  description?: string | null;
  nodes: SkillTreePrintNode[];
}

export interface SkillTreePrintInput {
  treeTitle: string;
  groups: SkillTreePrintGroup[];
  nodeCount: number;
  prereqLinkCount: number;
}

function esc(s: string | null | undefined): string {
  if (!s) return "";
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function buildPrintHTML(data: SkillTreePrintInput): string {
  const fmt = () =>
    new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  const groupsHtml = data.groups
    .map((g) => {
      const nodesHtml =
        g.nodes.length > 0
          ? `<ol class="node-list">
          ${g.nodes
            .map(
              (n) => `<li class="node-item">
              <div class="node-title-row">
                <span class="node-title ${n.isKeyNode ? "key-node" : ""}">${esc(n.title)}</span>
                ${n.isKeyNode ? `<span class="key-badge">Key assessment</span>` : ""}
              </div>
              ${n.description ? `<p class="node-desc">${esc(n.description)}</p>` : ""}
              ${
                n.contentSummary
                  ? `<p class="node-summary"><span class="summary-label">Summary</span>${esc(n.contentSummary)}</p>`
                  : ""
              }
              ${
                n.prereqTitles.length > 0
                  ? `<p class="node-prereqs"><span class="prereq-label">Prerequisites</span>${esc(n.prereqTitles.join(" · "))}</p>`
                  : ""
              }
            </li>`
            )
            .join("")}
        </ol>`
          : `<p class="empty-group">No skill nodes in this section yet.</p>`;

      return `<section class="group-block">
        <div class="group-header">
          <span class="group-badge">${esc(g.badge)}</span>
          <h2 class="group-title">${esc(g.title)}</h2>
        </div>
        ${g.description ? `<p class="group-desc">${esc(g.description)}</p>` : ""}
        ${nodesHtml}
      </section>`;
    })
    .join("");

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<title>${esc(data.treeTitle)} — Skill Tree</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');

  :root {
    --green: #1C3829;
    --green-mid: #2F6147;
    --cream: #EAE6DC;
    --cream-dark: #DDD8CB;
    --lime: #C8D46A;
    --text: #1C3829;
    --text-muted: #4A6B58;
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }

  body {
    font-family: 'DM Sans', Georgia, sans-serif;
    background: var(--cream);
    color: var(--text);
    line-height: 1.6;
    font-size: 13px;
  }

  .page {
    max-width: 820px;
    margin: 0 auto;
    padding: 0 32px 64px;
    background: var(--cream);
  }

  .header {
    background: var(--green);
    margin: 0 -32px;
    padding: 36px 32px 28px;
    position: relative;
  }
  .header-eyebrow {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--lime);
    margin-bottom: 10px;
  }
  .header-title {
    font-size: 28px;
    font-weight: 700;
    color: #ffffff;
    line-height: 1.2;
    margin-bottom: 10px;
    font-family: Georgia, serif;
  }
  .header-lede {
    font-size: 12.5px;
    color: rgba(255,255,255,0.78);
    max-width: 640px;
    line-height: 1.55;
    margin-bottom: 14px;
  }
  .header-meta {
    margin-top: 16px;
    padding-top: 14px;
    border-top: 1px solid rgba(255,255,255,0.12);
    font-size: 10px;
    color: rgba(255,255,255,0.52);
    letter-spacing: 0.04em;
    display: flex;
    flex-wrap: wrap;
    gap: 8px 20px;
  }

  .legend {
    margin-top: 28px;
    padding: 14px 18px;
    background: #fff;
    border-radius: 8px;
    border: 1px solid rgba(44,97,71,0.12);
    font-size: 11.5px;
    color: var(--text-muted);
    line-height: 1.55;
  }
  .legend strong { color: var(--text); font-weight: 600; }

  .group-block {
    margin-top: 28px;
    padding: 18px 20px 20px;
    background: #fff;
    border-radius: 10px;
    border: 1px solid rgba(44,97,71,0.12);
    box-shadow: 0 1px 0 rgba(28,56,41,0.04);
  }
  .group-header {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 8px;
  }
  .group-badge {
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #fff;
    background: var(--green);
    padding: 3px 9px;
    border-radius: 3px;
    white-space: nowrap;
  }
  .group-title {
    font-size: 17px;
    font-weight: 700;
    color: var(--text);
    font-family: Georgia, serif;
    flex: 1;
    min-width: 0;
  }
  .group-desc {
    font-size: 12px;
    color: var(--text-muted);
    margin-bottom: 14px;
    line-height: 1.6;
  }

  .node-list {
    list-style: none;
    counter-reset: sknode;
    display: flex;
    flex-direction: column;
    gap: 0;
  }
  .node-item {
    counter-increment: sknode;
    padding: 12px 12px 12px 16px;
    border-left: 3px solid rgba(44,97,71,0.22);
    margin-left: 6px;
    position: relative;
  }
  .node-item::before {
    content: counter(sknode);
    position: absolute;
    left: -10px;
    top: 14px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: var(--cream-dark);
    color: var(--green-mid);
    font-size: 10px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(44,97,71,0.15);
  }
  .node-item + .node-item {
    border-top: 1px dashed rgba(44,97,71,0.12);
    margin-top: 4px;
    padding-top: 16px;
  }
  .node-title-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 4px;
  }
  .node-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
  }
  .node-title.key-node { color: var(--green-mid); }
  .key-badge {
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    background: var(--lime);
    color: var(--green);
    padding: 2px 7px;
    border-radius: 3px;
  }
  .node-desc {
    font-size: 11.5px;
    color: var(--text-muted);
    line-height: 1.55;
    margin-top: 2px;
  }
  .node-summary {
    font-size: 11px;
    color: var(--text-muted);
    margin-top: 8px;
    padding: 8px 10px;
    background: var(--cream);
    border-radius: 5px;
    line-height: 1.5;
  }
  .summary-label {
    display: block;
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: var(--green-mid);
    margin-bottom: 4px;
  }
  .node-prereqs {
    font-size: 10.5px;
    color: var(--text-muted);
    margin-top: 8px;
    font-style: italic;
  }
  .prereq-label {
    font-style: normal;
    font-weight: 600;
    color: var(--green-mid);
    margin-right: 6px;
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  .empty-group {
    font-size: 12px;
    color: var(--text-muted);
    font-style: italic;
    padding: 8px 0;
  }

  .footer {
    margin-top: 40px;
    padding-top: 16px;
    border-top: 1px solid rgba(44,97,71,0.2);
    font-size: 10px;
    color: var(--text-muted);
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
  }

  @media print {
    @page { margin: 0.5in; size: letter portrait; }
    html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body { background: var(--cream); }
    .page { padding: 0 0 32px; max-width: 100%; }
    .header {
      margin: 0 0 20px;
      padding: 28px 24px 22px;
      border-radius: 0;
      break-after: avoid;
      page-break-after: avoid;
    }
    /* Avoid break-inside on whole group — large chapters would jump to the next page and leave a blank first page. */
    .node-item { break-inside: avoid; page-break-inside: avoid; }
    .print-btn { display: none !important; }
  }

  .print-btn {
    position: fixed;
    top: 20px;
    right: 24px;
    background: var(--green);
    color: var(--lime);
    border: none;
    padding: 10px 20px;
    border-radius: 6px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 7px;
    box-shadow: 0 4px 16px rgba(28,56,41,0.2);
    z-index: 100;
  }
  .print-btn:hover { background: #2F6147; }
</style>
</head>
<body>
<button type="button" class="print-btn" onclick="window.print()">
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
  Print / Save as PDF
</button>

<div class="page">
  <header class="header">
    <div class="header-eyebrow">Skill tree</div>
    <h1 class="header-title">${esc(data.treeTitle)}</h1>
    <p class="header-lede">
      Structured learning path with prerequisite relationships. The interactive graph in the app shows links visually;
      this document lists each node in order with prerequisite names for easy reading and printing.
    </p>
    <div class="header-meta">
      <span>${data.nodeCount} skill node${data.nodeCount !== 1 ? "s" : ""}</span>
      <span>${data.prereqLinkCount} prerequisite link${data.prereqLinkCount !== 1 ? "s" : ""}</span>
      <span>Printed ${fmt()}</span>
      <span>Distilled Intuition</span>
    </div>
  </header>

  <div class="legend">
    <strong>How to read this.</strong> Each section is a chapter or level from your course. Within a section, nodes are listed in teaching order.
    <strong>Prerequisites</strong> name the nodes a learner should complete before this one. <strong>Key assessment</strong> marks nodes with a required exam.
  </div>

  ${groupsHtml}

  <div class="footer">
    <span>${esc(data.treeTitle)} — Skill Tree</span>
    <span>distilledintuition.training</span>
  </div>
</div>
</body>
</html>`;
}

/** @returns false if the browser blocked the pop-up */
export function openSkillTreePrintWindow(data: SkillTreePrintInput): boolean {
  const html = buildPrintHTML(data);
  const w = window.open("", "_blank");
  if (!w) {
    return false;
  }
  w.document.write(html);
  w.document.close();
  return true;
}
